<!--
  HospitalAdManage.vue
  광고 병원 관리 페이지 (병원 관리 → 광고 병원 관리)
  - 게시 중인 광고 병원: 환자용 병원 목록 맨 위 + AD 표시, 후기 목록 맨 앞에 최신 후기 1건 고정
  - 같은 기간에는 게시된 광고가 한 곳만 있도록 서버가 검사한다
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>

    <!-- 페이지 헤더 (BannerManagement.vue 구조) -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">광고 병원 관리</h1>
          </div>
          <span class="pageDesc">
            게시 중인 광고 병원은 병원 목록 맨 위에 AD 표시와 함께 노출되고, 후기 목록 맨 앞에 최신 후기 1건이
            고정됩니다. 같은 기간에는 한 병원만 광고할 수 있습니다.
          </span>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 -->
    <div class="px-0 pb-2">
      <div class="card content-wrap-card">
        <HospitalAdTable
          :rows="rows"
          :loading="loading"
          @add="handleAdd"
          @edit="handleEdit"
          @toggle="handleToggle"
          @remove="handleRemove"
        />
      </div>
    </div>

    <HospitalAdModal
      :open="modal.open"
      :loading="modal.loading"
      :is-edit="modal.isEdit"
      :row="modal.row"
      @ok="handleModalOk"
      @cancel="modal.open = false"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { twoBtnDialog } from '@/utils/utils';
import HospitalAdApi from './api';
import HospitalAdTable from './components/HospitalAdTable.vue';
import HospitalAdModal from './components/HospitalAdModal.vue';

export default {
  name: 'HospitalAdManage',
  components: {
    HospitalAdTable,
    HospitalAdModal,
  },
  setup() {
    const rows = ref([]);
    const loading = ref(false);
    const modal = reactive({
      open: false,
      loading: false,
      isEdit: false,
      row: null,
    });

    // 서버가 거부한 이유(기간 겹침 등)는 그대로 보여 준다
    const showError = (error, defaultMessage) => {
      const msg = error.response?.data?.message || error.message || defaultMessage;
      const text = Array.isArray(msg) ? msg.join(', ') : msg;
      const status = error.response?.status;
      if (status && status >= 400 && status < 500) {
        message.warning(text, 5);
      } else {
        message.error(text);
      }
    };

    const loadList = async () => {
      loading.value = true;
      try {
        const response = await HospitalAdApi.getList();
        rows.value = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        showError(error, '광고 병원 목록을 불러오지 못했습니다.');
      } finally {
        loading.value = false;
      }
    };

    const handleAdd = () => {
      Object.assign(modal, { open: true, isEdit: false, row: null });
    };

    const handleEdit = (row) => {
      Object.assign(modal, { open: true, isEdit: true, row });
    };

    const handleModalOk = async (payload) => {
      modal.loading = true;
      try {
        if (modal.isEdit) {
          const { startDate, endDate, isFeatured } = payload;
          await HospitalAdApi.update(payload.companyId, { startDate, endDate, isFeatured });
          message.success('광고 병원을 수정했습니다.');
        } else {
          await HospitalAdApi.create(payload);
          message.success('광고 병원을 추가했습니다.');
        }
        modal.open = false;
        await loadList();
      } catch (error) {
        showError(error, '광고 병원을 저장하지 못했습니다.');
      } finally {
        modal.loading = false;
      }
    };

    const handleToggle = async (row) => {
      const next = !row.isFeatured;
      const confirmed = next
        ? await twoBtnDialog(
            '광고 게시',
            `${row.name} 광고를 ${row.startDate}부터 게시합니다. 기간 안이면 바로 병원 목록 맨 위에 AD 표시와 함께 노출됩니다.`,
            '게시',
          )
        : await twoBtnDialog(
            '광고 게시 해제',
            `${row.name} 광고를 내리면 병원 목록의 AD 표시와 맨 위 노출, 후기 맨 앞 고정이 바로 사라집니다.`,
            '게시 해제',
          );
      if (!confirmed) return;

      try {
        await HospitalAdApi.update(row.companyId, {
          startDate: row.startDate,
          endDate: row.endDate,
          isFeatured: next,
        });
        message.success(next ? `${row.name} 광고를 게시했습니다.` : `${row.name} 광고를 내렸습니다.`);
        await loadList();
      } catch (error) {
        showError(error, '게시 여부를 바꾸지 못했습니다.');
      }
    };

    const handleRemove = async (row) => {
      const confirmed = await twoBtnDialog(
        '광고 병원 삭제',
        `${row.name}을(를) 광고 병원 목록에서 삭제합니다. 게시 중이면 광고도 바로 내려갑니다.`,
        '삭제',
      );
      if (!confirmed) return;

      try {
        await HospitalAdApi.remove(row.companyId);
        message.success('광고 병원을 삭제했습니다.');
        await loadList();
      } catch (error) {
        showError(error, '광고 병원을 삭제하지 못했습니다.');
      }
    };

    onMounted(loadList);

    return {
      rows,
      loading,
      modal,
      handleAdd,
      handleEdit,
      handleModalOk,
      handleToggle,
      handleRemove,
    };
  },
};
</script>

<style scoped lang="scss">
/* BannerManagement.vue 헤더 스타일 */
.container-wrap {
  padding: 0 16px;
}

.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 0;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}
</style>
